/**
 * Selectors for fields where Space must keep inserting a space.
 * Children inside a rich-text or code editor match via `closest`.
 */
const EDITABLE_TARGET_SELECTOR =
  'input, textarea, select, [contenteditable="true"], [contenteditable=""], .monaco-editor, .cm-editor, .cm-content, .ProseMirror';

/**
 * Selectors for controls that already use Space to activate or toggle.
 * Includes Material select triggers (`combobox`) used across this app.
 */
const INTERACTIVE_TARGET_SELECTOR = [
  'button',
  'a',
  'input',
  'textarea',
  'select',
  '[role="button"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="slider"]',
  '[role="spinbutton"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Resolves the element that owns a keyboard event, including text nodes.
 * @param target - `KeyboardEvent.target`
 * @returns The element to test, or null when the target is not in the DOM
 */
function elementFromTarget(target: EventTarget | null): Element | null {
  if (target instanceof Element) {
    return target;
  }
  if (target instanceof Node) {
    return target.parentElement;
  }
  return null;
}

/**
 * Whether Space should keep its normal editing behavior.
 * Matches the target and any editable ancestor, including rich text and code editors.
 * @param target - `KeyboardEvent.target`
 * @returns True when the user is typing in an editable field
 */
export function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  const element = elementFromTarget(target);
  if (!element) {
    return false;
  }
  if (element instanceof HTMLElement && element.isContentEditable) {
    return true;
  }
  return element.closest(EDITABLE_TARGET_SELECTOR) != null;
}

/**
 * Whether Space should keep its normal activation behavior on a focused control.
 * Also skips custom widgets that already use Space (`tabindex` other than -1),
 * including table rows, collapsible cards, and key-value rows.
 * @param target - `KeyboardEvent.target`
 * @returns True when focus is inside a button, link, field, ARIA widget, or focusable control
 */
export function isInteractiveKeyboardTarget(target: EventTarget | null): boolean {
  const element = elementFromTarget(target);
  if (!element) {
    return false;
  }
  return element.closest(INTERACTIVE_TARGET_SELECTOR) != null;
}
